import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { CircleChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'hooks';
import type { TransportRouteListEntry } from 'contracts/transport-routes';
import { TransportRouteStatusIndicator } from 'pages/Settings/transport-routes/shared-components';
import { DataTableHelper } from 'utils';

import { translations } from './translations';

const dt = new DataTableHelper<TransportRouteListEntry>();

type HookOptions = {
  onClickNavigateToDetails?: (id: number) => void;
}

export const useTransportRouteColumns = ({
                                           onClickNavigateToDetails,
                                         }: HookOptions): ColumnDef<TransportRouteListEntry>[] => {
  const { getTranslation: t } = useTranslation(translations);

  const id = dt.buildColumn({
    id: 'Id',
    accessorKey: 'id',
    content: (row) => row.id,
  });
  const name = dt.buildColumn({
    id: t('name'),
    accessorKey: 'name',
    content: (row) => <div title={row.name} className={'truncate w-auto'}>{row.name}</div>,
  });
  const originIbge = dt.buildColumn({
    id: t('originIbge'),
    accessorFn: (row) => row.forward_trip.origin.ibge,
    content: (row) => row.forward_trip.origin.ibge,
  });
  const origin = dt.buildColumn({
    id: t('origin'),
    accessorFn: (row) => row.forward_trip.origin.id,
    content: (row) => {
      const { city_name, uf } = row.forward_trip.origin;
      return [city_name, uf].join('/');
    },
  });
  const destinationIbge = dt.buildColumn({
    id: t('destinationIbge'),
    accessorFn: (row) => row.forward_trip.destination.ibge,
    content: (row) => row.forward_trip.destination.ibge,
  });
  const destination = dt.buildColumn({
    id: t('destination'),
    accessorFn: (row) => row.forward_trip.destination.id,
    content: (row) => {
      const { city_name, uf } = row.forward_trip.destination;
      return [city_name, uf].join('/');
    },
  });
  const carrier = dt.buildColumn({
    id: t('carrier'),
    accessorFn: (row) => row.carrier_id,
    content: (row) => row.carrier.trade_name,
  });
  const statusIndicator = dt.buildColumn({
    id: t('status'),
    accessorKey: 'status',
    content: (row) => <TransportRouteStatusIndicator className={'max-w-[120px]'} status={row.status} />,
  });
  const navigationButton = dt.buildColumn({
    enableSorting: false,
    enableHiding: false,
    id: 'Open details',
    title: '',
    accessorFn: (row) => `open-details-${row.id}`,
    content: (row) => {
      return (
        <div className={'flex flex-row items-center'}>
          <button onClick={(e) => {
            onClickNavigateToDetails?.(row.id);
            e.preventDefault();
          }} className={'ml-auto'}>
            <CircleChevronRightIcon />
          </button>
        </div>
      );
    },
  });

  return [
    id,
    name,
    originIbge,
    origin,
    destinationIbge,
    destination,
    carrier,
    statusIndicator,
    navigationButton,
  ];
};